import { ColumnType, RowType } from 'react-final-table';
import { UseFormHandleSubmit } from 'react-hook-form';
import { QueryParamConfig } from 'use-query-params';
import { RefObject } from 'react';
import { SelectItemProps } from '../select/types';
import { Pagination } from '../../base-types';

export enum SortDir {
  ASC = 'asc',
  DESC = 'desc',
}

export enum FilterType {
  TEXT = 'text',
  CHECKBOX = 'checkbox',
  RADIO = 'radio',
  DATE = 'date',
  DATE_RANGE = 'date-range',
  AUTOCOMPLETE = 'autocomplete',
  AUTOCOMPLETE_MULTIPLE = 'autocomplete-muliple',
}

export interface Sorter {
  sortProp?: string;
  sortDir?: SortDir;
}

export interface SelectedOptionsTags {
  key: string;
  value: string;
  label?: string;
}

export interface Sort {
  sortProp?: string;
  sortDir?: string;
}

export type Filters = Record<
  string,
  {
    value: string[];
    type?: SchemaFilterType;
  }
>;

export interface FiltersSchema {
  name: string;
  type: FilterType;
  pickerType?: string;
  placeholder?: string;
  autocompleteList?: SelectItemProps[];
  withClearButton?: boolean;
  withoutCustomSelector?: boolean;
  withTimeSelector?: boolean;
  withTwoTimeSelector?: boolean;
  fields?: {
    name?: string;
    title: string;
    value?: string;
  }[];
}

export type ColumnProps = ColumnType<Record<string, unknown>> & {
  sortable?: boolean;
};

export interface TableProps {
  transactionTable?: boolean;
  title?: string;
  onRowsSelect?: (rows: string[], rowId?: string) => void;
  columns: ColumnProps[];
  columnsLabels: Record<string, unknown>;
  data: Record<any, any>[];
  filters: Filters;
  sort: Sort;
  filtersComponent?: {
    element: Record<string, React.JSX.Element>;
    list: Record<string, SelectItemProps[]>;
    withOverflow: Record<string, any>;
    withDateOverflow: Record<string, any>;
  };
  tableControlsClassName?: string;
  onSort: (name: string) => void;
  onFilterChange: (filters: Filters) => void;
  withSettings?: boolean;
  storeKey?: string;
  handleSubmit?: UseFormHandleSubmit<Filters>;
  topContent?: React.JSX.Element;
  filtersContent?: React.JSX.Element;
  isLoading?: boolean;
  expandable?: boolean;
  expandableColumns?: ColumnType<Record<string, unknown>>[];
  tableFormClassName?: string;
  tableTheadClassName?: string;
  tableHeadCellClassName?: string;
  tableBodyClassName?: string;
  tableClassName?: string;
  tableTopClassName?: string;
  withScroll?: boolean;
  pagination?: Pagination;
  setPage?: (data: { id: string; take: string }) => void;
  take?: string;
  onExpand?: (id: string) => void;
  isLoadingExpandableRow?: boolean;
  error?: string;
  paginationDisabled?: boolean;
  disabledRowIds?: string[];
  clickable?: boolean;
  onClearAutoComplete?: () => void;
  linkWithoutSearch?: boolean;
  onRowClick?: (data: string) => void;
  collapseAllRows?: boolean;
  withVerticalScroll?: boolean;
  isFetching?: boolean;
  hiddenFiltersTags?: string[];
  withDuplicateButton?: boolean;
  onDuplicateClick?: (id: string) => void;
  clearSelectedItems?: boolean;
  fullWidthTable?: boolean;
  savedFilters?: {
    id: string;
    name: string;
    value: Record<string, any>;
    default?: boolean;
    employeeId: string;
  }[];
  setSavedFilters?: (
    data: {
      id: string;
      name: string;
      value: Record<string, any>;
      default?: boolean;
      employeeId: string;
    }[],
  ) => void;
  savedFiltersKey?: string;
  selectedSavedFilter?: string;
  onSavedFilterClick?: (id: string, savedFilters: Filters) => void;
  setSelectedSavedFilter?: (id: string) => void;
  hiddedAllFilterTags?: boolean;
  userId?: string;
  isSuccessExpandableRow?: boolean;
  withSaveSelectedRows?: boolean;
  withExpandableContent?: boolean;
  expandableColspanValue?: number;
  nestedTable?: boolean;
  withToggleInput?: boolean;
  withSelectAllRowsOnExpand?: boolean;
  tableName?: string;
  newWindow?: boolean;
  rowsSelectDisabled?: boolean;
  selectedIds?: string[];
  onToggleAll?: (toggle: boolean) => void;
  withDeleteIcon?: boolean;
}

export interface FilterNavProps {
  lists?: Record<string, SelectItemProps[]>;
  tags: SelectedOptionsTags[];
  filters: Filters;
  foundedEntriesForTags?: number;
  onFilterChange: (filters: Filters) => void;
  onClearAutoComplete?: () => void;
  hiddenFiltersTags?: string[];
}

export type Row = RowType<{ id: string; [key: string]: unknown }>;

export interface TableRowProps {
  onRowClick?: (data: string) => void;
  expandable?: boolean;
  row: RowType<Record<any, any>>;
  isLoading?: boolean;
  onRowsSelect?: (rows: string[]) => void;
  selectRow: (row: Row) => void;
  expandableColumns?: ColumnType<Record<string, unknown>>[];
  columns?: ColumnType<Record<string, unknown>>[];
  onExpand: (id: string, rowId?: string) => void;
  expandedRows: string[];
  data: Record<any, any>;
  selected: boolean;
  isLoadingExpandableRow?: boolean;
  expandableColspan?: number;
  disabledRowIds?: string[];
  clickable?: boolean;
  linkWithoutSearch?: boolean;
  withDuplicateButton?: boolean;
  onDuplicateClick?: (id: string) => void;
  transactionTable?: boolean;
  hasDeleted?: boolean;
  withExpandableContent?: boolean;
  expandableColspanValue?: number;
  withToggleInput?: boolean;
  tableName?: string;
  newWindow?: boolean;
  rowsSelectDisabled?: boolean;
  withDeleteIcon?: boolean;
}

export enum SchemaFilterType {
  STRING = 'string',
  DATE = 'date',
  DATE_RANGE = 'date-range',
}

export type SchemaProps = Record<
  string,
  {
    value: QueryParamConfig<any, any>;
    type?: SchemaFilterType;
    queryNames?: string[];
    withTimeSelector?: boolean;
  }
>;

export interface SavedFilterTypes {
  item?: {
    id: string;
    name: string;
    value: Record<string, any>;
    default?: boolean;
    employeeId: string;
  };
  selectedSavedFilter?: string;
  onSavedFilterClick?: (id: string, savedFilters: Filters) => void;
  onFilterChange: (filters: Filters) => void;
  filters: Filters;
  setSelectedSavedFilter?: (data: string) => void;
  columnsLabels: Record<string, unknown>;
  lists?: Record<string, SelectItemProps[]>;
  savedFilters?: {
    id: string;
    name: string;
    value: Record<string, any>;
    default?: boolean;
    employeeId: string;
  }[];
  setSavedFilters?: (
    data: {
      id: string;
      name: string;
      value: Record<string, any>;
      default?: boolean;
      employeeId: string;
    }[],
  ) => void;
  savedFiltersKey?: string;
  itemIndex: number;
  userId?: string;
}

export interface SavedFiltersComponentTypes {
  savedFiltersKey?: string;
  savedFilters?: {
    id: string;
    name: string;
    value: Record<string, any>;
    default?: boolean;
    employeeId: string;
  }[];
  setSavedFilters?: (
    data: {
      id: string;
      name: string;
      value: Record<string, any>;
      default?: boolean;
      employeeId: string;
    }[],
  ) => void;
  userId?: string;
  selectedSavedFilter?: string;
  onSavedFilterClick?: (id: string, savedFilters: Filters) => void;
  onFilterChange: (filters: Filters) => void;
  filters: Filters;
  setSelectedSavedFilter?: (id: string) => void;
  columnsLabels: Record<string, unknown>;
  filtersComponent?: {
    element: Record<string, React.JSX.Element>;
    list: Record<string, SelectItemProps[]>;
    withOverflow: Record<string, any>;
    withDateOverflow: Record<string, any>;
  };
  setSaveFilterPopupVisible: (data: boolean) => void;
  onUploadFiltersPreset: (files: FileList) => void;
  uploadFiltersPresetRef: RefObject<HTMLInputElement | null>;
}

export interface TableFilterTypes {
  children: React.JSX.Element | React.JSX.Element[];
  column: ColumnType<Record<string, unknown>> & {
    filterModalPlacement?: string;
    withTimeSelector?: boolean;
  };
  isActive: boolean;
  filters?: Filters;
  withOverflow?: boolean;
  withDateOverflow?: boolean;
}

export interface TableHeadTypes {
  withScroll: boolean;
  tableHeadCellClassName?: string;
  filtersComponent?: {
    element: Record<string, React.JSX.Element>;
    list: Record<string, SelectItemProps[]>;
    withOverflow: Record<string, any>;
    withDateOverflow: Record<string, any>;
  };
  sort: Sort;
  onSort: (name: string) => void;
  filters: Filters;
  column: ColumnProps;
  elementIsVisibleInViewport: any;
  index: number;
  morePadding?: boolean;
  fullWidthTable?: boolean;
}
